import { useState, useCallback } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import { HoldToConfirmButton } from '@/components/HoldToConfirmButton'
import useTranslate from './use-translate'

/**
 * Confirmation enrichie, pour les actions qui méritent mieux que le libellé
 * générique : un titre qui dit ce qui va se passer, et un bouton rouge quand
 * l'action est destructrice et irréversible.
 */
export type ConfirmOptions = {
  message: string
  /** Remplace « Confirmation requise ». */
  title?: string
  /** Remplace « Valider ». */
  confirmLabel?: string
  /** Bouton de confirmation en rouge, et placé en dernier. */
  destructive?: boolean
  /** À maintenir au lieu de cliquer (TEC-1956) ; par défaut, toute action destructrice. */
  holdToConfirm?: boolean
}

export type ConfirmFunction = <T extends (...args: any[]) => any>(
  action: T,
  message: string | ConfirmOptions,
  ...params: Parameters<T>
) => ReturnType<T>

type ConfirmComponent = React.FC

const useConfirmation = (): [ConfirmFunction, ConfirmComponent] => {
  const { t: tComm } = useTranslate('common')
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [title, setTitle] = useState<string | undefined>(undefined)
  const [confirmLabel, setConfirmLabel] = useState<string | undefined>(
    undefined
  )
  const [destructive, setDestructive] = useState(false)
  const [holdToConfirm, setHoldToConfirm] = useState(false)
  const [actionParams, setActionParams] = useState<any[]>([])
  const [confirmAction, setConfirmAction] = useState<
    ((...args: any[]) => any) | null
  >(null)
  const [resolvePromise, setResolvePromise] = useState<
    ((value: any) => void) | null
  >(null)

  const handleOpen = useCallback(
    (action: (...args: any[]) => any, resolve: (value: any) => void) => {
      setConfirmAction(() => action)
      setResolvePromise(() => resolve)
      setOpen(true)
    },
    []
  )

  const handleClose = useCallback(() => {
    setOpen(false)
    setConfirmAction(null)
    setResolvePromise(null)
  }, [])

  const handleConfirmation = useCallback(() => {
    if (confirmAction) {
      const result = confirmAction(...actionParams) // eslint-disable-line
      if (resolvePromise) {
        resolvePromise(result)
      }
    }
    handleClose()
  }, [handleClose, confirmAction, resolvePromise, actionParams])

  const confirmation: ConfirmFunction = useCallback(
    <T extends (...args: any[]) => any>(
      action: T,
      message: string | ConfirmOptions,
      ...params: Parameters<T>
    ): ReturnType<T> => {
      const options: ConfirmOptions =
        typeof message === 'string' ? { message } : message
      setMessage(options.message)
      setTitle(options.title)
      setConfirmLabel(options.confirmLabel)
      setDestructive(options.destructive ?? false)
      setHoldToConfirm(options.holdToConfirm ?? options.destructive ?? false)
      setActionParams(params)

      return new Promise((resolve) => {
        handleOpen(action, resolve)
      }) as ReturnType<T> // eslint-disable-line
    },
    [handleOpen]
  )

  const ConfirmationModal = () => (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ p: 2.5, fontSize: '1rem' }}>
        {title ?? tComm('confirmationRequired')}
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        <DialogContentText
          id="alert-dialog-description"
          sx={{ fontSize: '0.8125rem', color: 'text.primary' }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          sx={{ textTransform: 'none' }}
        >
          {tComm('cancel')}
        </Button>
        {holdToConfirm ? (
          <HoldToConfirmButton
            label={confirmLabel ?? tComm('validate')}
            onConfirm={() => handleConfirmation()}
            color={destructive ? 'error' : 'primary'}
            data-testid="use-confirmation-confirm-button"
            sx={{ textTransform: 'none' }}
          />
        ) : (
          <Button
            onClick={() => handleConfirmation()}
            variant="contained"
            color={destructive ? 'error' : 'primary'}
            data-testid="use-confirmation-confirm-button"
            // Une action destructrice ne prend pas le focus par défaut : une
            // touche Entrée réflexe ne doit pas suffire à supprimer.
            autoFocus={!destructive}
            sx={{ textTransform: 'none' }}
          >
            {confirmLabel ?? tComm('validate')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )

  return [confirmation, ConfirmationModal]
}

export default useConfirmation
